import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MapPin, Plus, Edit, Ban, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchBar from '@/components/shared/SearchBar';
import { API, toast } from '@/App';
import { logClientError } from '@/utils/clientError';

const ManageAgentsPR = () => {
  const [agents, setAgents] = useState([]);
  const [relayPoints, setRelayPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    relay_point_id: ''
  });

  // Filtered agents based on search
  const filteredAgents = useMemo(() => {
    if (!searchQuery) return agents;
    
    const query = searchQuery.toLowerCase();
    return agents.filter(agent => 
      agent.full_name.toLowerCase().includes(query) ||
      agent.email.toLowerCase().includes(query) ||
      agent.phone?.toLowerCase().includes(query) ||
      agent.relay_point_name?.toLowerCase().includes(query)
    );
  }, [agents, searchQuery]);

  useEffect(() => {
    loadAgents();
    loadRelayPoints();
  }, []);

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${API}/admin/users/by-role/agent_pr`);
      setAgents(response.data);
    } catch (error) {
      logClientError('admin.manage_agents_pr.load', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelayPoints = async () => {
    try {
      const response = await axios.get(`${API}/admin/relay-points`);
      setRelayPoints(response.data?.items || []);
    } catch (error) {
      logClientError('admin.manage_agents_pr.load_relay_points', error);
    }
  };

  const getRelayPointName = (rpId) => {
    const rp = relayPoints.find(r => r.id === rpId);
    return rp ? rp.name : 'Non assigné';
  };

  const handleCreate = () => {
    setEditingAgent(null);
    setFormData({
      email: '',
      password: '',
      full_name: '',
      phone: '',
      relay_point_id: ''
    });
    setShowDialog(true);
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setFormData({
      email: agent.email,
      password: '',
      full_name: agent.full_name,
      phone: agent.phone,
      relay_point_id: agent.relay_point_id || ''
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingAgent) {
        await axios.patch(`${API}/admin/users/${editingAgent.id}`, {
          email: formData.email,
          full_name: formData.full_name,
          phone: formData.phone,
          relay_point_id: formData.relay_point_id
        });
        toast.success('Agent P-R mis à jour');
      } else {
        await axios.post(`${API}/admin/users/create`, {
          ...formData,
          role: 'agent_pr'
        });
        toast.success('Agent P-R créé avec succès');
      }
      setShowDialog(false);
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleActive = async (agentId, isActive) => {
    try {
      await axios.patch(`${API}/admin/users/${agentId}/toggle-active?is_active=${!isActive}`);
      toast.success(isActive ? 'Agent suspendu' : 'Agent activé');
      loadAgents();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet agent ?')) return;

    try {
      await axios.delete(`${API}/admin/users/${agentId}`);
      toast.success('Agent P-R supprimé');
      loadAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <MapPin className="w-6 h-6 text-cyan-600" />
                Gestion des Agents de Points de Relais
              </CardTitle>
              <CardDescription>Créer, modifier et gérer les agents P-R</CardDescription>
            </div>
            <Button
              data-testid="create-agent-btn"
              className="bg-cyan-500 hover:bg-cyan-600"
              onClick={handleCreate}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvel Agent
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          {agents.length > 0 && (
            <div className="mb-6">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher par nom, email, téléphone ou point de relais..."
                className="max-w-md"
              />
              {searchQuery && (
                <p className="text-sm text-gray-600 mt-2">
                  {filteredAgents.length} résultat(s) trouvé(s)
                </p>
              )}
            </div>
          )}

          {agents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun agent P-R pour le moment</p>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Aucun résultat trouvé pour "{searchQuery}"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Point de Relais</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAgents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell className="font-medium">{agent.full_name}</TableCell>
                    <TableCell>{agent.email}</TableCell>
                    <TableCell>{agent.phone}</TableCell>
                    <TableCell>{getRelayPointName(agent.relay_point_id)}</TableCell>
                    <TableCell>
                      {agent.is_active ? (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                          <CheckCircle className="w-3 h-3" />
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 flex items-center gap-1 w-fit">
                          <XCircle className="w-3 h-3" />
                          Suspendu
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-agent-${agent.id}`}
                          onClick={() => handleEdit(agent)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`toggle-agent-${agent.id}`}
                          onClick={() => handleToggleActive(agent.id, agent.is_active)}
                        >
                          {agent.is_active ? (
                            <Ban className="w-4 h-4 text-orange-600" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`delete-agent-${agent.id}`}
                          onClick={() => handleDelete(agent.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAgent ? 'Modifier l\'Agent P-R' : 'Nouvel Agent P-R'}
            </DialogTitle>
            <DialogDescription>
              {editingAgent ? 'Modifier les informations de l\'agent' : 'Créer un nouveau compte agent P-R'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name">Nom complet *</Label>
              <Input
                id="full_name"
                data-testid="agent-full-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="agent-email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            {!editingAgent && (
              <div>
                <Label htmlFor="password">Mot de passe *</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="agent-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingAgent}
                />
              </div>
            )}

            <div>
              <Label htmlFor="phone">Téléphone *</Label>
              <Input
                id="phone"
                data-testid="agent-phone"
                placeholder="+242..."
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Point de Relais *</Label>
              <Select
                value={formData.relay_point_id}
                onValueChange={(val) => setFormData({ ...formData, relay_point_id: val })}
              >
                <SelectTrigger data-testid="agent-relay-point">
                  <SelectValue placeholder="Sélectionnez un point de relais" />
                </SelectTrigger>
                <SelectContent>
                  {relayPoints.map(rp => (
                    <SelectItem key={rp.id} value={rp.id}>{rp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="submit-agent" className="bg-cyan-500 hover:bg-cyan-600">
                {editingAgent ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageAgentsPR;
